'use client'

import {usePathname} from 'next/navigation'
import {NextIntlClientProvider, useMessages} from 'next-intl'
import {FC, useEffect, useState} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {Modal} from '@/components/common/modal/Modal'
import {usePreferences} from '@/components/context/Preferences'
import {useLanguageWarningModalContent} from '@/content/languageWarningModal'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {isPartiallySupportedLocale, Locale} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {loadNamespace} from '@/utils/i18n/loadNamespace'
import {getPathLocales, parseToLocaleAndCleanedPath} from '@/utils/i18n/restrictedPaths'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {TranslationNamespace} from '@/utils/translations/types'

import {JurisdictionFlag} from '../jurisdiction/JurisdictionFlag'
import {ModalBody} from './layout/ModalBody'
import {ModalFooter} from './layout/ModalFooter'

/*
 * A partially supported language is served on only some pages. A link from one of them to a page
 * not served in that language lands on its English version - the middleware redirects a language a
 * page is not served in - and this notice says why, in the language the visitor came from. See
 * "The language warning" in docs/visitor-preferences.md.
 */

// Module-level: a change of language remounts the shell, and with it this component
let lastLanguage: Locale | null = null

// The previous page's language on a client-side navigation, else that of the same-site page that
// linked here. A reload or a step through history is no new arrival.
const getPreviousLanguage = (): Locale | null => {
  if (lastLanguage) {
    return lastLanguage
  }
  const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
  if (navigation?.type !== 'navigate' || !document.referrer) {
    return null
  }
  const referrer = new URL(document.referrer)
  return referrer.origin === window.location.origin
    ? parseToLocaleAndCleanedPath(referrer.pathname).locale
    : null
}

type Warning = {
  locale: Locale
  messages: Record<string, any>
}

export const LanguageWarningModal: FC = () => {
  const language = useLanguage()
  const pathname = usePathname()
  const messages = useMessages()
  const {isPreferencesPending} = usePreferences()
  const [warning, setWarning] = useState<Warning | null>(null)

  useEffect(() => {
    const previousLanguage = getPreviousLanguage()
    lastLanguage = language
    const {cleanedPath} = parseToLocaleAndCleanedPath(pathname ?? '/')
    if (
      !previousLanguage ||
      previousLanguage === language ||
      !isPartiallySupportedLocale(previousLanguage) ||
      getPathLocales(cleanedPath).includes(previousLanguage)
    ) {
      return
    }

    const show = async () => {
      try {
        setWarning({
          locale: previousLanguage,
          messages: await loadNamespace(previousLanguage, TranslationNamespace.page),
        })
        analytics.track('LanguageWarningModal opened', {language: previousLanguage})
      } catch {
        // Without its copy there is no notice; the page itself is unaffected
      }
    }
    void show()
  }, [language, pathname])

  // Its text names the visitor's region, so it waits until that is known
  if (!warning || isPreferencesPending) {
    return null
  }

  const close = () => {
    setWarning(null)
    analytics.track('LanguageWarningModal closed', {language: warning.locale})
  }

  return (
    // In the language the visitor came from, whose messages this page does not have
    <NextIntlClientProvider locale={warning.locale} messages={{...messages, ...warning.messages}}>
      <LanguageWarning close={close} />
    </NextIntlClientProvider>
  )
}

const flagSize = 58
const jurisdictions = [
  Jurisdiction.EU,
  Jurisdiction.CA,
  Jurisdiction.NZ,
  Jurisdiction.UK,
  Jurisdiction.US,
  Jurisdiction.HK,
  Jurisdiction.AU,
]

const LanguageWarning: FC<{close: () => void}> = ({close}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {origin} = usePreferences()
  const {title, description, buttonText} = useLanguageWarningModalContent()

  const originName = alpha2ToName(origin, language)

  return (
    <>
      <Modal isVisible closeModal={close} hasCloseIcon={false}>
        <ModalBody
          title={t(title)}
          titleVariant={TextVariant.h4}
          description={
            <Trans i18nKey={description} values={{origin: originName}}>
              text
              <Text variant={TextVariant.text14} element="span">
                text
              </Text>
            </Trans>
          }
          image={
            <div className="wrapper">
              {jurisdictions.map((jurisdiction) => (
                <JurisdictionFlag key={jurisdiction} jurisdiction={jurisdiction} size={flagSize} />
              ))}
            </div>
          }
          isCentered
          minWidth="400px"
        >
          <div className="footer-wrapper">
            <ModalFooter
              primaryButton={{
                type: 'button',
                text: t(buttonText),
                onClick: close,
                fullWidth: false,
              }}
              isCentered
            />
          </div>
        </ModalBody>
      </Modal>

      <style jsx>{`
        .wrapper {
          display: flex;
          justify-content: center;
          margin: 0 calc(-${theme.spacing[6]} - ${flagSize / 2}px) ${theme.spacing[5]};
          gap: ${theme.spacing[4]};
        }
        .footer-wrapper {
          margin-top: -${theme.spacing[3]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            margin-bottom: ${theme.spacing[4]};
          }
          .footer-wrapper {
            margin-top: -${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
